<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import SidenavItem from "./SidenavItem.vue";
import { isAkunDemo } from "@/utils/role-utils";

const store = useStore();
const route = useRoute();
const router = useRouter();

// Get role from Vuex store (secure - fetched from API)
const role = computed(() => store.getters['auth/userRole']);

const showTransferMenu = ref(false);

function toggleTransferMenu() {
  showTransferMenu.value = !showTransferMenu.value;
}

function navigateTo(path) {
  showTransferMenu.value = false;
  router.push(path);
}

function handleClickOutside(event) {
  const wrapper = document.querySelector('.transfer-wrapper');
  if (wrapper && !wrapper.contains(event.target)) {
    showTransferMenu.value = false;
  }
}

// Check if PPOB is enabled for this merchant
const isPpobEnabled = computed(() => store.getters['auth/isPpobEnabled']);

// Check if user is BI user
const isBiUser = computed(() => store.getters['auth/isBiUser']);

// Fetch user profile on mount to ensure role is loaded
onMounted(async () => {
  document.addEventListener('click', handleClickOutside);
  if (!store.getters['auth/userRole']) {
    await store.dispatch('auth/fetchUserProfile');
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Check if user has role to see transfer button (Finance or Admin)
const hasTransferRole = computed(() => ['Finance', 'Admin'].includes(role.value));

// menu master list
const menuItems = ref([
  {
    name: "Home",
    to: "/home",
    key: "home",
    icon: "/assets/icons/ic_home.png",
    allowedRoles: ["Admin", "IT", "Finance", "Staff"],
  },
  {
    name: "Balance",
    to: "/balance",
    key: "balance",
    icon: "/assets/icons/ic_wallet.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Topup",
    to: "/upload-ppob",
    key: "upload-ppob",
    icon: "/assets/icons/ic_wallet.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Transaction",
    to: "/transaction",
    key: "transaction",
    icon: "/assets/icons/ic_report.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Integrations",
    to: "/integrations",
    key: "integrations",
    icon: "/assets/icons/ic_integration.svg",
    allowedRoles: ["Admin", "IT"],
  },
  {
    name: "User Management",
    to: "/user-management",
    key: "user-management",
    icon: "/assets/icons/ic_user_management.svg",
    activeIcon: "/assets/icons/ic_user_management_active.svg",
    allowedRoles: ["Admin"],
  },
  {
    name: "Help",
    to: "/help-center",
    key: "help-center",
    icon: "/assets/icons/ic_help.png",
    allowedRoles: ["Admin", "IT", "Finance", "Staff"],
  },
]);

// filter berdasarkan role (using computed role from store)
// dan hide Balance & Integrations menu jika company_name === 'AKUN DEMO'
const filteredMenu = computed(() => {
  const isDemoAccount = isAkunDemo();
  return menuItems.value.filter((item) => {
    // Hide Balance and Integrations menu if company_name === 'AKUN DEMO'
    if ((item.key === 'balance' || item.key === 'integrations' || item.key === 'user-management') && isDemoAccount) {
      return false;
    }
    
    // Hide Balance menu if user is BI user
    if ((item.key === 'balance' || item.key === 'integrations' || item.key === 'user-management' || item.key === 'help-center') && isBiUser.value) {
      return false;
    }
    
    // Hide Topup (PPOB) menu if is_ppob is not enabled
    if (item.key === 'upload-ppob' && !isPpobEnabled.value) {
      return false;
    }
    
    return item.allowedRoles.includes(role.value);
  });
});

const getCurrentRoute = computed(() => {
  return route.path.split("/")[1];
});
</script>

<template>
  <aside class="sidebar fixed-start" id="sidenav-main">
    <div class="sidebar-body">
      <ul class="nav-list">
        <!-- Transfer button - show if user has Finance/Admin role -->
        <!-- Router guard will redirect to Forbidden if KYB not approved -->
        <li
          class="p-4 transfer-wrapper"
          v-if="hasTransferRole"
        >
          <button
            class="w-100 transfer-button transfer-text"
            @click.stop="toggleTransferMenu"
          >
            Transfer
            <img
              src="/assets/ic_send.png"
              alt="Send Icon"
              style="width: 24px; height: 24px; margin-left: 8px"
            />
          </button>

          <!-- Floating transfer type menu -->
          <div v-if="showTransferMenu" class="transfer-menu" @click.stop>
            <button class="transfer-menu-item" @click="navigateTo('/transfer')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="5" width="13" height="16" rx="2" stroke="#2D6A57" stroke-width="1.5" fill="none"/>
                <rect x="6" y="3" width="13" height="16" rx="2" stroke="#2D6A57" stroke-width="1.5" fill="white"/>
                <line x1="9" y1="8" x2="16" y2="8" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="9" y1="11" x2="16" y2="11" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="9" y1="14" x2="13" y2="14" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <span class="transfer-menu-label">Bulk Transfer</span>
            </button>
            <div class="transfer-menu-divider"></div>
            <button class="transfer-menu-item" @click="navigateTo('/single-transfer')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="3" width="14" height="18" rx="2" stroke="#2D6A57" stroke-width="1.5" fill="none"/>
                <line x1="8" y1="8" x2="16" y2="8" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="8" y1="11" x2="16" y2="11" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="8" y1="14" x2="12" y2="14" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <span class="transfer-menu-label">Single Transfer</span>
            </button>
          </div>
        </li>

        <hr />

        <!-- Loop menu items -->
        <li
          v-for="item in filteredMenu"
          :key="item.key"
          :class="{ active: getCurrentRoute === item.key }"
        >
          <sidenav-item :to="item.to" :icon="item.key">
            <template v-slot:icon>
              <img
                :src="
                  item.key === 'user-management' &&
                  getCurrentRoute === 'user-management'
                    ? item.activeIcon
                    : item.icon
                "
                alt=""
                width="24"
                height="24"
              />
            </template>
            {{ item.name }}
          </sidenav-item>
        </li>
      </ul>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  width: 250px;
  height: calc(100vh - 56px);
  margin-top: 56px;
  background-color: #fff;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  /* position: fixed; */
  top: 56px;
  /* left: 0; */
  /* z-index: 999; */
}

.sidebar-header {
  padding: 20px 16px;
  border-bottom: 1px solid #e9ecef;
}

.logo-container {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo {
  margin-right: 10px;
}

.logo-text {
  font-size: 18px;
  font-weight: 600;
  color: #4caf50;
}

.sidebar-body {
  flex: 1;
  padding: 16px 0;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 10px;
}

.nav-list li {
  margin-bottom: 4px;
}

.nav-list li.active {
  background-color: #e9fbf0;
  border-radius: 100px;
  color: #128050;
  font-weight: 600;
  text-decoration: none;
}

.transfer-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  border-radius: 100px;
  border: none;
  padding: 8px 16px 8px 24px;
  background-color: #43bb75;
}

.transfer-text {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 600;
  font-size: 16px;
  color: #fff;
  line-height: 150%;
  letter-spacing: 0%;
  vertical-align: middle;
}

.transfer-wrapper {
  position: relative;
}

.transfer-menu {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: calc(100% + 8px);
  width: 220px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  overflow: hidden;
}

.transfer-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.transfer-menu-item:hover {
  background-color: #f0faf5;
}

.transfer-menu-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #2d6a57;
  line-height: 1.4;
}

.transfer-menu-divider {
  height: 1px;
  background-color: #e9ecef;
  margin: 0 20px;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.badge-maintenance {
  position: absolute;
  top: 8px;
  right: 12px;
  background-color: orange;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: bold;
  z-index: 10;
  box-shadow: 0 0 4px rgba(220, 53, 69, 0.6);
  animation: pulse 1.5s infinite ease-in-out;
}
</style>
